<template>
  <teleport to="body">
    <div v-if="isOpen" class="modal-backdropers" @click="closeModal">
      <div class="modal fade show" :class="{ show: isOpen }" :style="{ display: isOpen ? 'block' : 'none' }"
        @click.stop>
        <div ref="dialogRef" v-bind:class="'modal-dialog ' + modalWidth" :style="{
          transform: `translate(${state.x}px, ${state.y}px)`
        }">
          <div class="modal-content">
            <!-- Slot para el encabezado del modal -->
            <div class="modal-drag-zone" :class="{ 'has-header': modalHead }" @mousedown="startDrag">
              <!-- Slot opcional -->
              <slot name="header" v-if="modalHead"></slot>
            </div>
            <!-- Cuerpo del modal (contenido dinámico) -->
            <div class="modal-body text-left p-0 scrollable-modal-body">
              <slot name="body"></slot>
            </div>
            <!-- Pie del modal con los botones de acción -->
            <div class="modal-footer" v-if="modalFooter">
              <slot name="footer"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
  import { onMounted, onBeforeUnmount, reactive, ref } from "vue";
  import { createDraggableModal } from "./useDragrable";
  defineProps<{
    isOpen: boolean,
    modalFooter: boolean,//[True] shows the modal footer
    modalHead: boolean//[True] shows the modal Head
    modalWidth?: string // opcional para el ancho del modal
  }>();

  const emit = defineEmits<{ (e: 'close'): void }>();

  const dialogRef = ref<HTMLElement | null>(null);
  const state = reactive({ x: 0, y: 0 });
  const draggable = createDraggableModal(() => dialogRef.value);

  const startDrag = (e: MouseEvent) => draggable.start(e);

  const syncPosition = () => {
    const pos = draggable.getPosition();
    state.x = pos.x;
    state.y = pos.y;
  };

  document.addEventListener("mousemove", syncPosition);

  const handleEsc = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  onMounted(() => document.addEventListener('keydown', handleEsc));
  onBeforeUnmount(() => {
    draggable.stop();
    document.removeEventListener("keydown", handleEsc);
    document.removeEventListener("mousemove", syncPosition);
  });

  const closeModal = () => { emit('close'); };
</script>

<style>
.draggable-header {
  cursor: move;
  user-select: none;
}

.modal-drag-zone {
  cursor: move;
  user-select: none;
}

.modal-drag-zone:not(.has-header) {
  height: 12px;
}

.modal-drag-zone.has-header {
  padding: 0;
}

.modal-drag-zone:hover {
  background: rgba(0, 0, 0, 0.03);
}

.modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 2px 6px rgba(0, 0, 0, 0.1);
  transition:
    box-shadow 0.2s ease,
    transform 0.1s ease;
}

.modal-content:active {
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.25),
    0 6px 12px rgba(0, 0, 0, 0.15);
}

.scrollable-modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.modal-content .modal-footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  min-height: 60px;
  height: auto;
  justify-content: center;
  flex-shrink: 0; /* evita que se comprima */
}

.modal-header {
  padding: 0.15rem 0.6rem;
  min-height: 50px;
  display: flex;
  align-items: center;
}

.modal-header>* {
  margin: 0;
}
</style>